import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, NavLink, useLocation, useHistory } from "react-router-dom";
import { TabType } from "src/enums";
import { Tab } from "src/models";
import { _ } from "src/services";
import {
  ConfigInit,
  ModalsAction,
  UserSession,
  UserSessionAction,
} from "src/store";
import {
  ArchiveFill,
  ArchiveOutline,
  ArrowDown,
  Devices,
  Favorite,
  Gift,
  History,
  LiveFill,
  LiveOutline,
  Logout,
  Package,
  ProfileFill,
  ProfileOutline,
  TvFill,
  TvOutline,
} from "../svg";
import { Appbar } from "./appbar";
import { Avatar } from "./avatar";
import { Button, Container } from "react-bootstrap";

export const Header: React.FC<{ hideForZarebin: boolean }> = ({
  hideForZarebin,
}) => {
  //#region dependencies

  const { pathname } = useLocation();
  const history = useHistory();
  const dispatch = useDispatch();

  //#endregion

  //#region properties

  const config: ConfigInit = useSelector((state: any) => state.ConfigState);
  const user: UserSession = useSelector((state: any) => state.UserState);
  const homeActiveFlag =
    pathname.toLowerCase().indexOf("/program") === -1 &&
    pathname.toLowerCase() !== "/";
  const authRef = useRef<HTMLDivElement>(null);

  //#endregion

  //#region states

  const [authMenuOpen, setAuthMenuOpen] = useState(false);

  //#endregion

  //#region methods

  const getLogo = (): string => {
    const logos = config.config.logos;

    return logos.Television;
  };

  const getLink = (type: TabType) => {
    switch (type) {
      case TabType.Archive:
        return "/archive";
      case TabType.Tv:
        return "/tv";
      case TabType.Live:
      default:
        return "/";
    }
  };

  const login = () => {
    dispatch(ModalsAction.showLogin());
  };

  const logout = () => {
    dispatch(ModalsAction.showLogout());
  };

  const toggleAuthMenu = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    e.stopPropagation();
    setAuthMenuOpen((state) => !state);
  };

  const openGift = () => dispatch(ModalsAction.showGift());

  //#endregion

  //#region helpers

  const renderLogo = () => {
    const programId = config.config.appSetting.singleProgramId;

    return (
      <div className="d-flex align-items-center navbar-main--part">
        <span className="logos-container ml-2 ml-md-0 d-block">
          <Link
            className="focusable"
            to={programId ? `/program/${programId}` : "/"}
          >
            <div className="logo-right-side float-right border-left-0">
              <img src={getLogo()} alt="logo" />
            </div>
          </Link>
        </span>
      </div>
    );
  };

  const renderTabIcons = ({ activeTabType, title }: Tab) => {
    switch (activeTabType) {
      case TabType.Archive:
        return (
          <>
            <span className="outline">
              <ArchiveOutline />
            </span>
            <span className="fill">
              <ArchiveFill />
            </span>
            <span>{title}</span>
          </>
        );
      case TabType.Tv:
        return (
          <>
            <span className="outline">
              <TvOutline />
            </span>
            <span className="fill">
              <TvFill />
            </span>
            <span>{title}</span>
          </>
        );
      case TabType.Live:
        return (
          <>
            <span className="outline">
              <LiveOutline />
            </span>
            <span className="fill">
              <LiveFill />
            </span>
            <span>{title}</span>
          </>
        );
    }
  };

  const TabLink = ({ origin, to, children, ...props }: any) =>
    origin ? (
      <a href={to} {...props}>
        {children}
      </a>
    ) : (
      <NavLink activeClassName="router-link-active" to={to} {...props}>
        {children}
      </NavLink>
    );

  const renderTabLink = (tab: Tab, index: number, isMobile: boolean) => {
    let className =
      tab.activeTabType === TabType.Live && homeActiveFlag
        ? " only-exact-active"
        : "";
    isMobile && (className = ` navbar--mobile__link ${className}`);

    return (
      <TabLink
        key={index}
        origin={tab.origin}
        className={`focusable nav-link text-rtl px-3 py-0${className}`}
        to={`${tab.origin || ""}${getLink(tab.activeTabType)}`}
      >
        {renderTabIcons(tab)}
      </TabLink>
    );
  };

  const renderTabs = () => {
    return (
      <div className="collapse navbar-expand d-none d-md-block navbar-main--part main-navigation">
        <ul className="navbar-nav p-0 d-flex justify-content-center">
          {config.config.appSetting.activeTabs.map((x, index) => (
            <li className="nav-item" key={index}>
              {renderTabLink(x, index, false)}
            </li>
          ))}
        </ul>
      </div>
    );
  };

  const renderMobialTabs = () => {
    return (
      <nav className="navbar--mobile navbar d-md-none">
        <div className="d-flex justify-content-around list-unstyled w-100 m-0 p-0 h-100 main-navigation container m-auto">
          {config.config.appSetting.activeTabs.map((x, index) =>
            renderTabLink(x, index, true)
          )}
          {user.loggedIn && !hideForZarebin && (
            <NavLink
              to="/profile"
              title={_("profile")}
              activeClassName="router-link-active"
              className="navbar--mobile__link focusable nav-link text-rtl"
            >
              <span className="outline">
                <ProfileOutline />
              </span>
              <span className="fill">
                <ProfileFill />
              </span>
              <span>{_("profile")}</span>
            </NavLink>
          )}
        </div>
      </nav>
    );
  };

  const renderAuthButtons = () => {
    if (hideForZarebin) return null;
    const subsUrl = user.user?.hasCurrentSubscription
      ? "/profile/subscription"
      : `/subscription/${
          config.config?.appSetting?.singleSubscriptionId || ""
        }`;
    const packageButton = config.config.appSetting.isPackagingSystemEnabled && (
      <Link className="btn btn-packages package-link" to={subsUrl}>
        <Package size={16} />
        <span>
          {_(user.user?.hasCurrentSubscription ? "yourPackages" : "packages")}
        </span>
      </Link>
    );

    if (!user.loggedIn) {
      return (
        <>
          {/* {packageButton}
          <div className="d-md-block">
            <button
              className="btn btn-sm btn-outline-primary header-login-button focusable text-rtl"
              onClick={login}
            >
              <span>{_("login")}</span>
            </button>
          </div> */}
        </>
      );
    }
    const isHome = pathname.toLowerCase().indexOf("/program") === -1;
    return (
      <Button
        variant="light"
        style={{ width: "60px" }}
        onClick={() =>
          isHome ? dispatch(UserSessionAction.logout()) : history.goBack()
        }
      >
        {isHome ? _("logout") : "بازگشت"}
      </Button>
    );
  };

  const renderAuth = () => {
    return (
      <div className="d-flex align-items-center navbar-main--part">
        <div className="mr-auto">
          <div className="header-member-territory d-flex">
            {renderAuthButtons()}
          </div>
        </div>
      </div>
    );
  };

  //#endregion

  //#region  effects

  useEffect(() => {
    const handelClickAnywhere = () => {
      if (authMenuOpen) setAuthMenuOpen(false);
    };

    document.addEventListener("click", handelClickAnywhere);

    return () => {
      document.removeEventListener("click", handelClickAnywhere);
    };
  }, [authMenuOpen]);

  //#endregion
  const isHome = pathname.toLowerCase().indexOf("/program") === -1;
  return (
    <>
      {/* <Appbar />
      <div className="app-theme2">
        <nav className="navbar navbar-expand-lg navbar-light fixed-top navbar--main">
          <div className="container large-container main-navbar-container d-flex justify-content-between px-0 ">
            {renderLogo()}
            {config.loaded && renderTabs()}
            {config.loaded && renderAuth()}
          </div>
          {config.loaded && renderMobialTabs()}
        </nav>
      </div> */}
      {isHome && (
        <Container
          fluid
          style={{
            display: "flex",
            justifyContent: "space-between",
            padding: "10px",
          }}
        >
          <img src={getLogo()} alt="logo" />
          {pathname.toLowerCase().indexOf("/login") === -1 && (
            <Button
              style={{ width: "15%" }}
              variant="outline-danger"
              onClick={() => dispatch(UserSessionAction.logout())}
            >
              {_("logout")}
            </Button>
          )}
        </Container>
      )}
    </>
  );
};
